import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import { Loader2 } from "lucide-react";

export function ProtectedRoute({
  children,
  requireAdmin = false,
}: {
  children: React.ReactNode;
  requireAdmin?: boolean;
}) {
  const [session, setSession] = useState<Session | null>(null);
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (!session) {
        setAuthorized(false);
        setLoading(false);
      } else {
        setTimeout(() => checkAccess(session), 0);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (!session) {
        setAuthorized(false);
        setLoading(false);
      } else {
        checkAccess(session);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAccess = async (session: Session) => {
    try {
      const { data, error } = await supabase.functions.invoke("check-admin");
      if (error) {
        console.error("Admin check failed:", error);
        setAuthorized(false);
      } else {
        const isAdmin = data?.is_admin === true;
        const isAgent = data?.role === "agent";
        setAuthorized(requireAdmin ? isAdmin : isAdmin || isAgent);
      }
    } catch (err) {
      console.error("Admin check error:", err);
      setAuthorized(false);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!session || !authorized) {
    return <Navigate to="/team-login" replace />;
  }

  return <>{children}</>;
}
